import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Buildings, Plus, Users, FileText, HardDrives, PencilSimple, DownloadSimple, UploadSimple, MagnifyingGlass, ArrowsClockwise, Phone, MapPin, CheckSquare, Square, Trash, EnvelopeSimple, Wrench, Warning } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

export default function Companies() {
  const { user: me } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", main_contact_name: "", reference_emails_text: "", address: "", city: "", postal_code: "", country: "France", main_phone: "", notes: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [importReport, setImportReport] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [migrateReport, setMigrateReport] = useState(null);
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrateOverwrite, setMigrateOverwrite] = useState(false);
  const [migrateSelected, setMigrateSelected] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const importInputRef = useRef(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairItems, setRepairItems] = useState([]);
  const [repairEdits, setRepairEdits] = useState({});
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairCount, setRepairCount] = useState(0);

  const loadCorrupted = async () => {
    try {
      const r = await api.get("/companies/detect-corrupted");
      setRepairCount(r.data?.count || 0);
      if (repairOpen) {
        setRepairItems(r.data?.items || []);
        // Seed edits with current (corrupt) values so admin can retype
        const seed = {};
        (r.data?.items || []).forEach((it) => {
          seed[it.id] = {
            name: it.name || "",
            main_contact_name: it.main_contact_name || "",
            address: it.address || "",
            city: it.city || "",
            notes: it.notes || "",
          };
        });
        setRepairEdits(seed);
      }
    } catch { /* silent — admin badge */ }
  };
  useEffect(() => { if (me?.role === "admin") loadCorrupted(); }, [me]);
  useEffect(() => { if (repairOpen) loadCorrupted(); }, [repairOpen]);

  const setRepairField = (id, field, value) => {
    setRepairEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const stillCorrupt = (v) => typeof v === "string" && (v.includes("\ufffd") || v.includes("\u00f0"));

  const saveRepairs = async () => {
    // Build patches only for items where the admin actually changed something AND the new value has no marker
    const patches = [];
    repairItems.forEach((it) => {
      const edits = repairEdits[it.id] || {};
      const fields = {};
      it.corrupt_fields.forEach((f) => {
        const nv = (edits[f] || "").trim();
        if (nv && !stillCorrupt(nv) && nv !== (it[f] || "")) fields[f] = nv;
      });
      if (Object.keys(fields).length > 0) patches.push({ id: it.id, fields });
    });
    if (patches.length === 0) { toast.info("Aucune modification à enregistrer"); return; }
    setRepairBusy(true);
    try {
      const r = await api.post("/companies/repair-corrupted", { patches });
      toast.success(`${r.data.updated} société${r.data.updated > 1 ? "s" : ""} corrigée${r.data.updated > 1 ? "s" : ""}`);
      await loadCorrupted();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setRepairBusy(false); }
  };

  // v284 — Pagination serveur (25/page). Recherche debounced envoyée au
  // backend en param `q` pour matcher name/contact/city/domaine partout,
  // pas seulement sur la page courante.
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (search.trim()) params.q = search.trim();
      const r = await api.get("/companies", { params });
      // Paged response {items, total, pages, ...} always returned when we send params.
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setCompanies(items);
      setTotal(Number(r.data.total ?? items.length));
      setPages(Math.max(1, Number(r.data.pages ?? 1)));
    }
    catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  // Reset to page 1 whenever the search term changes; a small debounce keeps
  // the API quiet while the user types.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  const create = async (e) => {
    e.preventDefault();
    const emails = (form.reference_emails_text || "").split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    try {
      await api.post("/companies", { ...form, reference_emails: emails });
      toast.success("Société créée");
      setOpen(false);
      setForm({ name: "", main_contact_name: "", reference_emails_text: "", address: "", city: "", postal_code: "", country: "France", main_phone: "", notes: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      name: c.name || "",
      main_contact_name: c.main_contact_name || "",
      reference_emails_text: (c.reference_emails || []).join("\n"),
      main_phone: c.main_phone || "",
      address: c.address || "",
      postal_code: c.postal_code || "",
      city: c.city || "",
      country: c.country || "France",
      notes: c.notes || "",
    });
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    const emails = (editForm.reference_emails_text || "").split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    try {
      await api.patch(`/companies/${editing.id}`, { ...editForm, reference_emails: emails });
      toast.success("Société mise à jour");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (c) => {
    const links = [];
    if (c.contacts_count) links.push(`${c.contacts_count} contact${c.contacts_count > 1 ? "s" : ""}`);
    if (c.contracts_count) links.push(`${c.contracts_count} contrat${c.contracts_count > 1 ? "s" : ""}`);
    if (c.equipment_count) links.push(`${c.equipment_count} équipement${c.equipment_count > 1 ? "s" : ""}`);
    const detachMsg = links.length
      ? `\n\n${links.join(" · ")} rattaché(s) seront détaché(s) — ils ne seront PAS supprimés.`
      : "";
    if (!window.confirm(`Supprimer définitivement la société "${c.name}" ?${detachMsg}`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Société supprimée");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Suppression impossible"); }
  };

  const toggleSelect = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleSelectAllVisible = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      filtered.forEach((c) => { if (checked) next.add(c.id); else next.delete(c.id); });
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const items = companies.filter((c) => selected.has(c.id));
    const totalLinks = items.reduce((acc, c) => ({
      contacts: acc.contacts + (c.contacts_count || 0),
      contracts: acc.contracts + (c.contracts_count || 0),
      equipment: acc.equipment + (c.equipment_count || 0),
    }), { contacts: 0, contracts: 0, equipment: 0 });
    const linkParts = [];
    if (totalLinks.contacts) linkParts.push(`${totalLinks.contacts} contact${totalLinks.contacts > 1 ? "s" : ""}`);
    if (totalLinks.contracts) linkParts.push(`${totalLinks.contracts} contrat${totalLinks.contracts > 1 ? "s" : ""}`);
    if (totalLinks.equipment) linkParts.push(`${totalLinks.equipment} équipement${totalLinks.equipment > 1 ? "s" : ""}`);
    const detachMsg = linkParts.length
      ? `\n\n${linkParts.join(" · ")} rattaché(s) au total seront détaché(s) — ils ne seront PAS supprimés.`
      : "";
    const preview = items.slice(0, 5).map((c) => `• ${c.name}`).join("\n");
    const more = items.length > 5 ? `\n… et ${items.length - 5} autre${items.length - 5 > 1 ? "s" : ""}` : "";
    if (!window.confirm(`Supprimer définitivement ${items.length} société${items.length > 1 ? "s" : ""} ?\n\n${preview}${more}${detachMsg}\n\nCette action est irréversible.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/companies/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} société${r.data.deleted > 1 ? "s" : ""} supprimée${r.data.deleted > 1 ? "s" : ""}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression en bloc impossible");
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/companies/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `societes_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${companies.length} sociétés exportées`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export impossible");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/companies/import.csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(r.data);
      toast.success(`${r.data.created} créée(s) · ${r.data.updated} mise(s) à jour · ${r.data.skipped} ignorée(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const runMigration = async (dry_run) => {
    setMigrateBusy(true);
    try {
      const body = { dry_run, overwrite: migrateOverwrite };
      if (!dry_run) body.only_user_ids = Array.from(migrateSelected);
      const r = await api.post("/companies/migrate-users", body);
      setMigrateReport(r.data);
      if (dry_run) {
        // Pre-check every candidate (opt-out model — safer default)
        const allIds = new Set();
        (r.data.by_company || []).forEach((c) => c.users.forEach((u) => u.user_id && allIds.add(u.user_id)));
        setMigrateSelected(allIds);
      } else {
        toast.success(`${r.data.linked} utilisateur(s) rattaché(s) à leur société`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration impossible");
    } finally { setMigrateBusy(false); }
  };

  const toggleMigrateOne = (uid, checked) => {
    setMigrateSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(uid); else next.delete(uid);
      return next;
    });
  };
  const toggleMigrateCompany = (companyGroup, checked) => {
    setMigrateSelected((prev) => {
      const next = new Set(prev);
      companyGroup.users.forEach((u) => {
        if (!u.user_id) return;
        if (checked) next.add(u.user_id); else next.delete(u.user_id);
      });
      return next;
    });
  };

  // v284 — Filtre local supprimé au profit de la recherche serveur (param `q`).
  // On garde `filtered` = liste courante paginée pour minimiser les diff de rendu.
  const filtered = companies;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Portefeuille clients</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Sociétés</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Regroupe les contacts par entreprise via leur domaine email — un ticket arrivant de <span className="mono">jean@acme.fr</span> sera automatiquement rattaché à la société ACME et à ses contrats.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {me?.role === "admin" && selected.size > 0 && (
            <>
              <span className="text-xs text-muted-foreground mr-1" data-testid="companies-selection-count">
                {selected.size} sélectionnée{selected.size > 1 ? "s" : ""}
              </span>
              <Button variant="secondary" size="sm" onClick={clearSelection} data-testid="companies-clear-selection">
                Annuler
              </Button>
              <Button variant="destructive" size="sm" onClick={bulkDelete} disabled={bulkBusy}
                data-testid="companies-bulk-delete">
                <Trash size={14} className="mr-1.5"/>{bulkBusy ? "Suppression…" : `Supprimer ${selected.size}`}
              </Button>
              <span className="text-muted-foreground mx-1">·</span>
            </>
          )}
          {me?.role === "admin" && (
            <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="companies-export-csv">
              <DownloadSimple size={14} className="mr-1.5"/> Export CSV
            </Button>
          )}
          {me?.role === "admin" && repairCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRepairOpen(true)}
              className="border-orange-500 text-orange-700 hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-950/30"
              data-testid="companies-repair-btn"
              title="Corriger les caractères corrompus (ð / �) dans les données existantes"
            >
              <Wrench size={14} className="mr-1.5"/> Réparer {repairCount} donnée{repairCount > 1 ? "s" : ""}
            </Button>
          )}
          {me?.role === "admin" && (
            <>
              <Button variant="secondary" size="sm" onClick={() => runMigration(true)}
                disabled={migrateBusy} data-testid="companies-migrate-btn"
                title="Rattacher tous les utilisateurs à leur société via leur domaine email">
                <ArrowsClockwise size={14} className="mr-1.5"/>{migrateBusy ? "Analyse…" : "Migrer utilisateurs"}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => importInputRef.current?.click()}
                disabled={importBusy} data-testid="companies-import-csv">
                <UploadSimple size={14} className="mr-1.5"/>{importBusy ? "Import…" : "Import CSV"}
              </Button>
              <input ref={importInputRef} type="file" accept=".csv,.txt,text/csv" className="hidden"
                data-testid="companies-import-input"
                onChange={(e) => importCsv(e.target.files?.[0])}/>
            </>
          )}
          {me?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-company-btn"><Plus size={16} className="mr-2"/> Nouvelle société</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display">Nouvelle société</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label>Nom de la société *</Label>
                  <Input required data-testid="company-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="ACME Holding"/>
                </div>
                <div>
                  <Label>Emails de référence *</Label>
                  <p className="text-xs text-muted-foreground mb-1">Un email par ligne. Les domaines seront extraits automatiquement pour rattacher les futurs tickets.</p>
                  <Textarea data-testid="company-ref-emails" rows={3}
                    value={form.reference_emails_text}
                    onChange={(e) => setForm({...form, reference_emails_text: e.target.value})}
                    placeholder={"sophie@acme.fr\ncontact@acme-groupe.com"}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Téléphone principal</Label><Input value={form.main_phone} onChange={(e) => setForm({...form, main_phone: e.target.value})}/></div>
                  <div className="md:col-span-2"><Label>Nom du contact principal</Label><Input placeholder="Ex. Sophie Bernard" value={form.main_contact_name || ""} onChange={(e) => setForm({...form, main_contact_name: e.target.value})} data-testid="company-main-contact"/></div>
                  <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/></div>
                  <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({...form, postal_code: e.target.value})}/></div>
                  <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})}/></div>
                  <div className="col-span-2"><Label>Pays</Label><Input value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}/></div>
                </div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/></div>
                <DialogFooter>
                  <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" data-testid="company-create">Créer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </header>

      <Card>
        <CardContent className="pt-6 relative">
          <MagnifyingGlass size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid="company-search" className="pl-9"
            placeholder="Rechercher société, adresse, ville, téléphone, code postal, notes…"
            value={search} onChange={(e) => setSearch(e.target.value)}/>
        </CardContent>
      </Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
        <table className="w-full text-sm table-auto min-w-[1240px]">
          <thead className="text-xs uppercase text-muted-foreground border-b">
            <tr>
              {me?.role === "admin" && (
                <th className="w-10 py-3 pl-4 pr-1">
                  <input
                    type="checkbox"
                    className="accent-primary cursor-pointer"
                    data-testid="companies-select-all"
                    checked={filtered.length > 0 && filtered.every((c) => selected.has(c.id))}
                    ref={(el) => {
                      if (!el) return;
                      const someSelected = filtered.some((c) => selected.has(c.id));
                      const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
                      el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                    aria-label="Tout sélectionner"
                  />
                </th>
              )}
              <th className="text-left py-3 px-4">Société</th>
              <th className="text-left py-3 px-4">Nom contact</th>
              <th className="text-left py-3 px-4">Email principal</th>
              <th className="text-left py-3 px-4 whitespace-nowrap">Téléphone</th>
              <th className="text-center py-3 px-4 w-16">Adresse</th>
              <th className="text-right py-3 px-4">Contacts</th>
              <th className="text-right py-3 px-4">Contrats</th>
              <th className="text-right py-3 px-4">Équipements</th>
              <th className="text-left py-3 px-4">Notes</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={me?.role === "admin" ? 11 : 10} className="text-center py-10 text-muted-foreground">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && companies.length > 0 && <tr><td colSpan={me?.role === "admin" ? 11 : 10} className="text-center py-10 text-muted-foreground">Aucun résultat pour « {search} ».</td></tr>}
            {!loading && companies.length === 0 && <tr><td colSpan={me?.role === "admin" ? 11 : 10} className="text-center py-10 text-muted-foreground">Aucune société. Créez-en une pour regrouper vos contacts et contrats.</td></tr>}
            {filtered.map((c) => {
              const addressLine = [c.address, [c.postal_code, c.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
              const primaryEmail = (c.reference_emails || [])[0] || "";
              const isSelected = selected.has(c.id);
              return (
              <tr key={c.id} className={`border-b hover:bg-muted/40 align-top ${isSelected ? "bg-primary/5" : ""}`} data-testid={`company-row-${c.id}`}>
                {me?.role === "admin" && (
                  <td className="py-3 pl-4 pr-1">
                    <input
                      type="checkbox"
                      className="accent-primary cursor-pointer mt-1"
                      data-testid={`select-company-${c.id}`}
                      checked={isSelected}
                      onChange={(e) => toggleSelect(c.id, e.target.checked)}
                      aria-label={`Sélectionner ${c.name}`}
                    />
                  </td>
                )}
                <td className="py-3 px-4 font-medium">
                  <Link to={`/societes/${c.id}`} className="text-primary hover:underline inline-flex items-center gap-2">
                    <Buildings size={16}/> {c.name}
                  </Link>
                </td>
                <td className="py-3 px-4 text-sm" data-testid={`company-contact-${c.id}`}>
                  {c.main_contact_name ? c.main_contact_name : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-3 px-4 mono text-xs" data-testid={`company-email-${c.id}`}>
                  {primaryEmail ? (
                    <a href={`mailto:${primaryEmail}`} className="text-primary hover:underline inline-flex items-center gap-1">
                      <EnvelopeSimple size={11}/> {primaryEmail}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-3 px-4 mono text-xs whitespace-nowrap" data-testid={`company-phone-${c.id}`}>
                  {c.main_phone ? (
                    <a href={`tel:${c.main_phone.replace(/\s+/g, "")}`}
                       className="text-primary hover:underline inline-flex items-center gap-1">
                      <Phone size={11}/> {c.main_phone}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-center" data-testid={`company-address-${c.id}`}>
                  {addressLine ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([c.address, c.postal_code, c.city, c.country].filter(Boolean).join(", "))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-primary hover:bg-primary/10 hover:scale-110 transition"
                      title={`Ouvrir dans Google Maps : ${addressLine}`}
                      aria-label={`Localisation ${c.name}`}
                      data-testid={`company-address-link-${c.id}`}
                    >
                      <MapPin size={18} weight="fill"/>
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right"><span className="inline-flex items-center gap-1"><Users size={12}/>{c.contacts_count}</span></td>
                <td className="py-3 px-4 text-right"><span className="inline-flex items-center gap-1"><FileText size={12}/>{c.contracts_count}</span></td>
                <td className="py-3 px-4 text-right"><span className="inline-flex items-center gap-1"><HardDrives size={12}/>{c.equipment_count}</span></td>
                <td className="py-3 px-4 text-xs max-w-[260px]" data-testid={`company-notes-${c.id}`}>
                  {c.notes ? (
                    <span className="text-muted-foreground line-clamp-2 whitespace-pre-wrap break-words" title={c.notes}>
                      {c.notes}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  {me?.role === "admin" && (
                  <>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Modifier"
                      onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                      <PencilSimple size={14}/>
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-red-50 dark:hover:bg-red-950/30"
                      title="Supprimer"
                      onClick={() => remove(c)} data-testid={`del-company-${c.id}`}>
                      <Trash size={14}/>
                    </Button>
                  </>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
          <div className="text-muted-foreground" data-testid="companies-total">{total} société(s)</div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="companies-prev-page">Précédent</Button>
            <span className="mono" data-testid="companies-page-indicator">Page {page} / {pages}</span>
            <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="companies-next-page">Suivant</Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Modifier · {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-3" data-testid="edit-company-form">
              <div>
                <Label>Nom de la société *</Label>
                <Input required data-testid="edit-company-name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})}/>
              </div>
              <div>
                <Label>Emails de référence</Label>
                <p className="text-xs text-muted-foreground mb-1">Un email par ligne. Les domaines seront réextraits automatiquement.</p>
                <Textarea data-testid="edit-company-emails" rows={3}
                  value={editForm.reference_emails_text}
                  onChange={(e) => setEditForm({...editForm, reference_emails_text: e.target.value})}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Téléphone principal</Label><Input value={editForm.main_phone} onChange={(e) => setEditForm({...editForm, main_phone: e.target.value})}/></div>
                <div className="md:col-span-2"><Label>Nom du contact principal</Label><Input placeholder="Ex. Sophie Bernard" value={editForm.main_contact_name || ""} onChange={(e) => setEditForm({...editForm, main_contact_name: e.target.value})} data-testid="edit-company-main-contact"/></div>
                <div><Label>Adresse</Label><Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})}/></div>
                <div><Label>Code postal</Label><Input value={editForm.postal_code} onChange={(e) => setEditForm({...editForm, postal_code: e.target.value})}/></div>
                <div><Label>Ville</Label><Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})}/></div>
                <div className="col-span-2"><Label>Pays</Label><Input value={editForm.country} onChange={(e) => setEditForm({...editForm, country: e.target.value})}/></div>
              </div>
              <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})}/></div>
              <DialogFooter>
                <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Annuler</Button>
                <Button type="submit" data-testid="edit-company-save">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Import CSV report dialog */}
      <Dialog open={!!importReport} onOpenChange={(v) => { if (!v) setImportReport(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Rapport d&apos;import CSV</DialogTitle>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4" data-testid="companies-import-report">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="text-2xl font-bold text-emerald-600">{importReport.created}</div>
                  <div className="text-xs uppercase text-muted-foreground">créées</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-blue-50 dark:bg-blue-950/30">
                  <div className="text-2xl font-bold text-blue-600">{importReport.updated}</div>
                  <div className="text-xs uppercase text-muted-foreground">mises à jour</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-muted/40">
                  <div className="text-2xl font-bold text-muted-foreground">{importReport.skipped}</div>
                  <div className="text-xs uppercase text-muted-foreground">ignorées</div>
                </div>
              </div>
              {importReport.errors?.length > 0 && (
                <div className="border border-destructive/40 rounded-lg p-3 bg-destructive/5">
                  <div className="text-sm font-semibold text-destructive mb-1.5">
                    {importReport.errors.length} erreur(s)
                  </div>
                  <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                    {importReport.errors.map((e, i) => (
                      <li key={i}>Ligne {e.line} · {e.name || "—"} · {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setImportReport(null)} data-testid="companies-import-close">Fermer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Migration users → companies report dialog */}
      <Dialog open={!!migrateReport} onOpenChange={(v) => { if (!v) setMigrateReport(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ArrowsClockwise size={20}/>
              {migrateReport?.dry_run ? "Aperçu de la migration utilisateurs → sociétés" : "Migration terminée"}
            </DialogTitle>
          </DialogHeader>
          {migrateReport && (
            <div className="space-y-4" data-testid="companies-migrate-report">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3 text-center bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="text-2xl font-bold text-emerald-600" data-testid="migrate-linked-count">{migrateReport.linked}</div>
                  <div className="text-xs uppercase text-muted-foreground">{migrateReport.dry_run ? "à rattacher" : "rattachés"}</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-blue-50 dark:bg-blue-950/30">
                  <div className="text-2xl font-bold text-blue-600">{migrateReport.unchanged}</div>
                  <div className="text-xs uppercase text-muted-foreground">déjà OK</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-amber-50 dark:bg-amber-950/30">
                  <div className="text-2xl font-bold text-amber-600">{migrateReport.no_matching_company}</div>
                  <div className="text-xs uppercase text-muted-foreground">sans société</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-muted/40">
                  <div className="text-2xl font-bold text-muted-foreground">{migrateReport.generic_domain_skipped}</div>
                  <div className="text-xs uppercase text-muted-foreground">domaine générique</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Analysé : <b>{migrateReport.scanned_users}</b> utilisateurs actifs · <b>{migrateReport.active_companies_with_domains}</b> sociétés actives
                {migrateReport.overwrite ? " · mode réécriture activé" : ""}
              </div>

              {migrateReport.by_company?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <div className="label-caps mb-2 flex items-center justify-between">
                    <span>Par société ({migrateReport.by_company.length})</span>
                    <span className="text-[0.7rem] text-muted-foreground normal-case" data-testid="migrate-selected-count">
                      {migrateReport.dry_run ? `${migrateSelected.size} sélectionné(s) sur ${migrateReport.linked}` : `${migrateReport.linked} rattaché(s)`}
                    </span>
                  </div>
                  <div className="max-h-80 overflow-y-auto space-y-2">
                    {migrateReport.by_company.map((c) => {
                      const allChecked = c.users.every((u) => !u.user_id || migrateSelected.has(u.user_id));
                      const someChecked = c.users.some((u) => u.user_id && migrateSelected.has(u.user_id));
                      return (
                      <div key={c.company_id} className="border-b last:border-b-0 pb-2 last:pb-0" data-testid={`migrate-company-${c.company_id}`}>
                        <div className="text-sm font-semibold flex items-center gap-2">
                          {migrateReport.dry_run && (
                            <button type="button"
                              onClick={() => toggleMigrateCompany(c, !allChecked)}
                              title={allChecked ? "Tout décocher pour cette société" : "Tout cocher pour cette société"}
                              className="p-0.5 text-primary hover:bg-primary/10 rounded"
                              data-testid={`migrate-toggle-company-${c.company_id}`}>
                              {allChecked ? <CheckSquare size={16} weight="fill"/> : someChecked ? <CheckSquare size={16}/> : <Square size={16}/>}
                            </button>
                          )}
                          <Buildings size={13}/> {c.company_name}
                          <span className="text-xs text-muted-foreground">→ {c.count} utilisateur{c.count > 1 ? "s" : ""}</span>
                        </div>
                        <ul className="text-xs pl-5 mt-1 space-y-0.5">
                          {c.users.map((u) => {
                            const checked = u.user_id ? migrateSelected.has(u.user_id) : false;
                            return (
                            <li key={u.user_id || u.email} className={`flex items-center gap-2 ${!checked && migrateReport.dry_run ? "opacity-50" : ""}`}>
                              {migrateReport.dry_run && u.user_id && (
                                <input type="checkbox"
                                  checked={checked}
                                  onChange={(e) => toggleMigrateOne(u.user_id, e.target.checked)}
                                  className="accent-primary shrink-0"
                                  data-testid={`migrate-toggle-user-${u.user_id}`}/>
                              )}
                              <span className="mono text-muted-foreground">{u.email}</span>
                              {u.full_name && <span className="text-muted-foreground">— {u.full_name}</span>}
                            </li>
                            );
                          })}
                          {c.count > c.users.length && (
                            <li className="italic text-muted-foreground">… +{c.count - c.users.length} autres (tous inclus)</li>
                          )}
                        </ul>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {migrateReport.unmatched_sample?.length > 0 && (
                <details className="border rounded-lg p-3 bg-amber-50/40 dark:bg-amber-950/20">
                  <summary className="text-sm cursor-pointer font-semibold text-amber-900 dark:text-amber-300">
                    {migrateReport.no_matching_company} utilisateurs sans société correspondante (aperçu)
                  </summary>
                  <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto text-xs">
                    {migrateReport.unmatched_sample.map((u, i) => (
                      <li key={i}>
                        <span className="mono">{u.email}</span> — domaine <span className="mono">{u.domain}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[0.7rem] text-muted-foreground mt-2">
                    Ajoutez leur domaine dans la fiche de la société correspondante puis relancez la migration.
                  </p>
                </details>
              )}

              {migrateReport.dry_run && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  <input type="checkbox" id="migrate-overwrite" data-testid="migrate-overwrite"
                    checked={migrateOverwrite}
                    onChange={(e) => setMigrateOverwrite(e.target.checked)}
                    className="accent-primary"/>
                  <label htmlFor="migrate-overwrite" className="text-sm">
                    Ré-écrire aussi les utilisateurs déjà rattachés
                    <span className="block text-[0.7rem] text-muted-foreground">Utile si des utilisateurs ont été rattachés à la mauvaise société.</span>
                  </label>
                </div>
              )}

              <DialogFooter className="pt-2">
                {migrateReport.dry_run ? (
                  <>
                    <Button variant="secondary" onClick={() => setMigrateReport(null)} data-testid="migrate-cancel">Annuler</Button>
                    {migrateSelected.size > 0 ? (
                      <Button onClick={() => runMigration(false)} disabled={migrateBusy} data-testid="migrate-apply">
                        Appliquer — rattacher {migrateSelected.size} utilisateur{migrateSelected.size > 1 ? "s" : ""}
                      </Button>
                    ) : (
                      <Button disabled data-testid="migrate-apply-empty">Aucun utilisateur coché</Button>
                    )}
                  </>
                ) : (
                  <Button onClick={() => setMigrateReport(null)} data-testid="migrate-close">Fermer</Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="repair-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Wrench size={20} className="text-orange-600"/> Réparer les données corrompues
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/20 p-3 text-sm">
              <div className="flex items-start gap-2">
                <Warning size={18} className="text-orange-600 shrink-0 mt-0.5"/>
                <div>
                  <div className="font-medium text-orange-800 dark:text-orange-300">
                    {repairItems.length} société{repairItems.length > 1 ? "s" : ""} avec des caractères corrompus (<span className="mono">ð</span> ou <span className="mono">�</span>)
                  </div>
                  <div className="text-orange-700/90 dark:text-orange-300/80 text-xs mt-1">
                    Retapez la valeur correcte dans chaque champ. Les champs contenant encore <span className="mono">ð</span> ou <span className="mono">�</span> après édition seront ignorés. Le bouton &laquo;&nbsp;Enregistrer&nbsp;&raquo; applique toutes les corrections en une fois.
                  </div>
                </div>
              </div>
            </div>
            {repairItems.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">Aucune donnée corrompue détectée.</div>
            )}
            {repairItems.map((it) => {
              const edits = repairEdits[it.id] || {};
              return (
                <Card key={it.id} data-testid={`repair-row-${it.id}`}>
                  <CardContent className="pt-4 space-y-2">
                    <div className="text-xs text-muted-foreground mono">ID {it.id?.slice(0, 8)}…</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {it.corrupt_fields.map((f) => {
                        const label = f === "name" ? "Nom société"
                          : f === "main_contact_name" ? "Nom contact principal"
                          : f === "address" ? "Adresse"
                          : f === "city" ? "Ville"
                          : "Notes";
                        const original = it[f] || "";
                        const val = edits[f] ?? "";
                        const stillBad = stillCorrupt(val);
                        return (
                          <div key={f}>
                            <Label className="text-xs">{label}</Label>
                            <div className="text-xs text-red-600 dark:text-red-400 mono truncate" title={original}>
                              Corrompu : {original}
                            </div>
                            <Input
                              value={val}
                              onChange={(e) => setRepairField(it.id, f, e.target.value)}
                              className={stillBad ? "border-red-500" : val && val !== original ? "border-emerald-500" : ""}
                              data-testid={`repair-input-${it.id}-${f}`}
                              placeholder="Retapez la valeur correcte…"
                            />
                            {stillBad && (
                              <div className="text-xs text-red-600 mt-1">Contient encore un caractère corrompu.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRepairOpen(false)} data-testid="repair-close">Fermer</Button>
            <Button onClick={saveRepairs} disabled={repairBusy || repairItems.length === 0} data-testid="repair-save">
              <Wrench size={14} className="mr-2"/>{repairBusy ? "Enregistrement…" : "Enregistrer les corrections"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
